import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import DeleteIcon from '@src/svgs/DeleteIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import SparkleIcon from '@src/svgs/SparkleIcon';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { CLIP_PREVIEW_Z } from '../clipPreviewZ';

/** Where a right-click landed, and on which Chapter. */
export type ReelChapterMenuTarget = {
  chapterId: string;
  x: number;
  y: number;
};

/** Wide enough for the longest label; a pointer position cannot set this. */
const MENU_WIDTH = 200;

/** One item: 8px padding either side of a 15px line, plus the 2px row gap. */
const MENU_ITEM_HEIGHT = 35;

/** The surface's own padding, top and bottom. */
const MENU_PADDING = 8;

/** Why the item stands down while this Chapter's own run is in flight. */
const GENERATING_TITLE_REASON = 'Already generating a name for this chapter';

interface ReelChapterContextMenuProps {
  target: ReelChapterMenuTarget;
  onClose: () => void;
  /**
   * Opens the heading's own title field — the pencil, reached from the menu.
   * Above Generate Name, so the menu runs "name it yourself" → "have it named"
   * → "take it out": the two naming actions together, lightest first.
   */
  onRename?: (chapterId: string) => void;
  /** Names the Chapter from the Sections inside it (PLAT-11064). */
  onGenerateName?: (chapterId: string) => void;
  /** A run is already in flight for this Chapter; the heading shows it (DS1). */
  isGeneratingName?: boolean;
  /** Takes the boundary out, which folds this chapter's Sections into the one above. */
  onRemove?: (chapterId: string) => void;
  /**
   * False renders Remove disabled rather than dropping it, the reading the
   * Section menu takes: the opening chapter cannot go while others remain, and
   * a menu whose items come and go is harder to learn than one that greys them.
   */
  canRemove?: boolean;
  /** Why Remove is unavailable, as its native tooltip. */
  removeDisabledReason?: string;
}

/**
 * PLAT-11064 — the Chapter heading's action surface.
 *
 * A small twin of `ReelSectionContextMenu` rather than a widening of it (DS6):
 * that menu is typed on a `sectionId` target and every item, prop and disabled
 * rule in it is per-Section, so a `section | chapter` union would make four
 * settled items ask "which kind is this?" to answer nothing. The two share a look
 * and a dismissal contract by sharing `ContextMenuSurface` / `ContextMenuItem`,
 * not by sharing a component.
 *
 * Regenerate and Remove, reached from the heading's kebab as well as a
 * right-click. Remove moved here off the heading when the heading became a card:
 * a hover-revealed delete on the title line is a control the creator has to find
 * by sweeping the row, where the kebab says up front that there are actions and
 * holds both of them. Rename stays on the title (PLAT-11055) — it is the title.
 *
 * The four things PLAT-11060 had to get right are the same four here, and are
 * copied from it deliberately: pointer positioning through
 * {@link clampMenuToViewport} (`useAnchoredDropdown` positions from a trigger
 * rect, and a right-click has a point), a portal to `document.body` (the Section
 * list scrolls and the panel sits inside the clip-preview modal),
 * `useOutsideAlerter` plus dismiss-on-scroll, and a **capture-phase** Escape so
 * closing the menu does not close the whole clip preview with it.
 */
export const ReelChapterContextMenu: React.FC<ReelChapterContextMenuProps> = ({
  target,
  onClose,
  onRename,
  onGenerateName,
  isGeneratingName = false,
  onRemove,
  canRemove = true,
  removeDisabledReason,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // The shared hook, never a hand-rolled `document.addEventListener('mousedown')`
  // (CLAUDE.md): it also treats focus moving into the renderer iframe as an
  // outside click, which a `contains()` check on its own misses.
  useOutsideAlerter(menuRef, onClose);

  useEffect(() => {
    // Capture phase, so this runs before the clip-preview modal's own `document`
    // keydown — which would otherwise take the whole clip preview down with the
    // menu. Both listeners sit on `document`, where registration order would
    // decide it, and the modal's is registered first.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    // The menu is `position: fixed` and the list scrolls under it, so a held-open
    // menu would detach from its heading. Capture, so a scroll in any ancestor
    // container counts and not only the window's.
    const onScroll = () => onClose();

    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const items = [onRename, onGenerateName, onRemove].filter(Boolean).length;
  const { x, y } = clampMenuToViewport(
    target.x,
    target.y,
    MENU_WIDTH,
    items * MENU_ITEM_HEIGHT + MENU_PADDING,
  );

  /** Closes before it acts, the ordering the Section menu takes. */
  const pick = (action: () => void) => {
    onClose();
    action();
  };

  return createPortal(
    <ContextMenuSurface
      ref={menuRef}
      role="menu"
      data-testid="reel-chapter-context-menu"
      style={{
        top: y,
        left: x,
        width: MENU_WIDTH,
        zIndex: CLIP_PREVIEW_Z.reelChapterContextMenu,
      }}
    >
      {onRename && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          data-testid="reel-chapter-menu-rename"
          onClick={() => pick(() => onRename(target.chapterId))}
        >
          {/* The pencil the title already carries on hover — the same control,
              said out loud, for a creator who came to the kebab looking for it. */}
          <SimplePencilIcon width="14" height="14" />
          Edit Name
        </ContextMenuItem>
      )}
      {onGenerateName && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          $disabled={isGeneratingName}
          title={isGeneratingName ? GENERATING_TITLE_REASON : undefined}
          aria-disabled={isGeneratingName || undefined}
          data-testid="reel-chapter-menu-generate-name"
          onClick={() => {
            if (isGeneratingName) return;
            pick(() => onGenerateName(target.chapterId));
          }}
        >
          {/* The same glyph the Section item takes: the same action, one level
              up, and the icon this codebase already reads as "AI wrote this". */}
          <SparkleIcon width="14" height="14" strokeColor="currentColor" />
          Generate Name with AI
        </ContextMenuItem>
      )}
      {onRemove && (
        <ContextMenuItem
          type="button"
          role="menuitem"
          $danger
          $disabled={!canRemove}
          // Still hoverable while disabled, so the native tooltip can say why.
          title={canRemove ? undefined : removeDisabledReason}
          aria-disabled={!canRemove || undefined}
          data-testid="reel-chapter-menu-remove"
          onClick={() => {
            if (!canRemove) return;
            pick(() => onRemove(target.chapterId));
          }}
        >
          <DeleteIcon />
          Remove Chapter
        </ContextMenuItem>
      )}
    </ContextMenuSurface>,
    document.body,
  );
};
